import React from 'react';
import { Card } from '@/components/ui/card';
import { useMemo, useState, useCallback } from 'react';
import TableSkeleton from '@/components/ui/TableSkeleton';
import { TrophyIcon, ChevronUp, ChevronDown, ArrowUpDown, DollarSign, Target, TrendingUp, TrendingDown } from 'lucide-react';
import { 
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue
} from '@/components/ui/select';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { logger } from '@/utils/logger';

const EnhancedResultsTable = ({ 
  races: racesRaw = [], 
  appliedStrategy, 
  onRaceSelect, 
  selectedRaces = [], 
  showStrategyColumns = true,
  isLoading = false,
  error = null 
}) => {
  // Ensure races is always an array
  const races = Array.isArray(racesRaw) ? racesRaw : [];
  const [selectedDate, setSelectedDate] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Debug logging
  logger.log('EnhancedResultsTable - races:', races);
  logger.log('EnhancedResultsTable - appliedStrategy:', appliedStrategy);

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <Card className="p-4 text-center text-red-500">
        Error loading race data: {error.message}
      </Card>
    );
  }

  // Safety check for races prop (using raw prop for debugging)
  if (!Array.isArray(racesRaw)) {
    logger.error('EnhancedResultsTable - races prop is not an array:', racesRaw);
    logger.error('EnhancedResultsTable - races type:', typeof racesRaw);
    logger.error('EnhancedResultsTable - races constructor:', racesRaw?.constructor?.name);
    logger.error('EnhancedResultsTable - races is React element:', React.isValidElement(racesRaw));
    
    // Check if it's a React component
    const isReactComponent = React.isValidElement(racesRaw) || 
                            (typeof racesRaw === 'function') ||
                            (racesRaw && racesRaw.$$typeof);
    
    return (
      <Card className="p-4 text-center text-yellow-600">
        <div className="text-sm">
          Invalid race data format. Expected an array but received: {typeof racesRaw}
          {isReactComponent && ' (React Component detected)'}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Please check the data source and ensure it returns an array of race objects.
        </div>
        <div className="text-xs text-gray-400 mt-2">
          Constructor: {racesRaw?.constructor?.name || 'unknown'}
        </div>
        {!isReactComponent && (
          <div className="text-xs text-gray-400 mt-1">
            Debug info: {String(racesRaw).substring(0, 200)}...
          </div>
        )}
      </Card>
    );
  }

  const getRaceDate = (race) => {
    if (!race || typeof race !== 'object') return '';
    
    // First try to get date directly
    if (race.date && typeof race.date === 'string') return race.date;
    
    // Try to extract from race ID
    const raceId = race.id || '';
    if (typeof raceId !== 'string') return '';
    
    const parts = raceId.split('-');
    if (parts.length < 3) return '';
    
    const dd = parts[parts.length - 3];
    const mm = parts[parts.length - 2];
    const yyyy = parts[parts.length - 1];
    
    // Validate the extracted parts
    if (dd && mm && yyyy && 
        /^\d{1,2}$/.test(dd) && 
        /^\d{1,2}$/.test(mm) && 
        /^\d{4}$/.test(yyyy)) {
      return `${dd}-${mm}-${yyyy}`;
    }
    
    return '';
  };

  const availableDates = useMemo(() => {
    // races is now guaranteed to be an array
    
    const list = races
      .map(getRaceDate)
      .filter(date => date && typeof date === 'string' && date.includes('-'));
    const unique = Array.from(new Set(list));
    const toDate = (str) => {
      if (!str || typeof str !== 'string') {
        logger.warn('Invalid date string passed to toDate:', str);
        return new Date(0);
      }
      const parts = str.split('-');
      if (parts.length !== 3) {
        logger.warn('Invalid date format passed to toDate:', str);
        return new Date(0);
      }
      const [dd, mm, yyyy] = parts;
      return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    };
    return unique.sort((a, b) => toDate(b) - toDate(a));
  }, [races]);

  const filteredRaces = useMemo(() => {
    // races is now guaranteed to be an array
    
    const raceList = races;
    const filtered = !selectedDate
      ? raceList
      : raceList.filter((race) => getRaceDate(race) === selectedDate);

    const toDate = (str) => {
      if (!str) return new Date(NaN);
      const [dd, mm, yyyy] = str.split('-');
      return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    };

    // Apply sorting if configured
    let sorted = filtered.slice();
    
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aValue, bValue;
        
        switch (sortConfig.key) {
          case 'date':
            aValue = toDate(getRaceDate(a)).getTime() || 0;
            bValue = toDate(getRaceDate(b)).getTime() || 0;
            break;
          case 'track':
            aValue = a.meetingName || '';
            bValue = b.meetingName || '';
            break;
          case 'race':
            const aParts = (a.id || '').split('-');
            const bParts = (b.id || '').split('-');
            aValue = parseInt(aParts[aParts.length - 4], 10) || 0;
            bValue = parseInt(bParts[bParts.length - 4], 10) || 0;
            break;
          case 'horses':
            aValue = a.betfairResults?.numberOfRunners || 0;
            bValue = b.betfairResults?.numberOfRunners || 0;
            break;
          case 'strategyProfit':
            aValue = a.calculatedMetrics?.profit || 0;
            bValue = b.calculatedMetrics?.profit || 0;
            break;
          case 'strategyRoi':
            aValue = a.calculatedMetrics?.roi || 0;
            bValue = b.calculatedMetrics?.roi || 0;
            break;
          case 'strategyStake':
            aValue = a.calculatedMetrics?.totalStake || 0;
            bValue = b.calculatedMetrics?.totalStake || 0;
            break;
          default:
            return 0;
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue);
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sorting: date desc, then race number asc, then track name
      sorted.sort((a, b) => {
        const aDateStr = getRaceDate(a);
        const bDateStr = getRaceDate(b);
        const aTime = toDate(aDateStr).getTime();
        const bTime = toDate(bDateStr).getTime();
        const safeATime = isNaN(aTime) ? 0 : aTime;
        const safeBTime = isNaN(bTime) ? 0 : bTime;

        if (safeBTime !== safeATime) return safeBTime - safeATime; // date desc

        const aParts = (a.id || '').split('-');
        const bParts = (b.id || '').split('-');
        const aRaceNum = parseInt(aParts[aParts.length - 4], 10) || 0;
        const bRaceNum = parseInt(bParts[bParts.length - 4], 10) || 0;
        if (aRaceNum !== bRaceNum) return aRaceNum - bRaceNum; // race # asc

        const aTrack = a.meetingName || aParts.slice(0, aParts.length - 4).join(' ');
        const bTrack = b.meetingName || bParts.slice(0, bParts.length - 4).join(' ');
        return (aTrack || '').localeCompare(bTrack || '');
      });
    }

    return sorted;
  }, [races, selectedDate, sortConfig]);

  // Row selection handlers
  const handleRowSelect = useCallback((race) => {
    if (onRaceSelect) {
      onRaceSelect(race);
    }
  }, [onRaceSelect]);

  const isRowSelected = useCallback((raceId) => {
    return selectedRaces.includes(raceId);
  }, [selectedRaces]);

  // Row expansion handlers
  const toggleRowExpansion = useCallback((raceId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(raceId)) {
        newSet.delete(raceId);
      } else {
        newSet.add(raceId);
      }
      return newSet;
    });
  }, []);

  const isRowExpanded = useCallback((raceId) => {
    return expandedRows.has(raceId);
  }, [expandedRows]);

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-3 h-3 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-3 h-3 text-gray-600" />
      : <ChevronDown className="w-3 h-3 text-gray-600" />;
  };

  // Helper functions from original table
  const calculateUniqueTips = (tips) => {
    const validTips = tips.filter(tip => tip !== '-' && tip !== '');
    const uniqueTips = [...new Set(validTips)];
    return uniqueTips.length;
  };

  const getHorseNumber = (selectionId, allTips) => {
    if (!selectionId || !allTips?.length) return '-';
    const horse = allTips.find(tip => tip.selectionId === selectionId);
    return horse?.formattedName?.split('.')[0] || '-';
  };

  const calculateMaxHedge = (earlyTips, raceTips) => {
    if (!Array.isArray(earlyTips) || !Array.isArray(raceTips)) return '-';
    
    const allTips = [...earlyTips, ...raceTips];
    const uniqueTips = new Map();
    
    allTips.forEach(tip => {
      if (tip?.formattedName) {
        const horseNumber = tip.formattedName.split('.')[0];
        const odds = tip.lastPriceTraded || tip.currentPrice;
        if (horseNumber !== '-' && odds && !isNaN(odds)) {
          uniqueTips.set(horseNumber, parseFloat(odds));
        }
      }
    });

    if (uniqueTips.size === 0) return '-';

    let reciprocalSum = 0;
    uniqueTips.forEach(odds => {
      reciprocalSum += 1 / odds;
    });

    return reciprocalSum.toFixed(4);
  };

  // Strategy visualization helpers
  const renderStrategyIndicator = (race) => {
    if (!showStrategyColumns || !appliedStrategy) return null;

    const strategyApplication = race.strategyApplications?.[0];
    if (!strategyApplication) return null;

    const { applied, bets, profit } = strategyApplication;

    if (!applied) {
      return (
        <Badge variant="outline" className="text-gray-500">
          Not Applied
        </Badge>
      );
    }

    const winningBets = bets.filter(bet => bet.result === 'win' || bet.result === 'place');
    const losingBets = bets.filter(bet => bet.result === 'loss');
    
    const profitClass = profit > 0 ? 'text-green-600 bg-green-50 border-green-200' : 
                      profit < 0 ? 'text-red-600 bg-red-50 border-red-200' : 
                      'text-gray-600 bg-gray-50 border-gray-200';

    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          {winningBets.length > 0 && (
            <Badge variant="outline" className="text-green-600 bg-green-50 border-green-200">
              <TrophyIcon className="w-3 h-3 mr-1" />
              {winningBets.length}W
            </Badge>
          )}
          {losingBets.length > 0 && (
            <Badge variant="outline" className="text-red-600 bg-red-50 border-red-200">
              {losingBets.length}L
            </Badge>
          )}
        </div>
        <Badge variant="outline" className={profitClass}>
          {profit >= 0 ? '+' : ''}${profit.toFixed(2)}
        </Badge>
      </div>
    );
  };

  const renderBetDetails = (race) => {
    if (!showStrategyColumns || !appliedStrategy) return null;

    const strategyApplication = race.strategyApplications?.[0];
    if (!strategyApplication?.applied || !strategyApplication.bets.length) return null;

    return (
      <div className="mt-2 space-y-2">
        {strategyApplication.bets.map((bet, index) => {
          const isWinning = bet.result === 'win' || bet.result === 'place';
          const profitLoss = bet.return - bet.stake;
          
          return (
            <div 
              key={index} 
              className={`flex items-center justify-between text-xs p-3 rounded-lg border ${
                isWinning 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <Badge variant="outline" className="text-xs px-1 py-0">
                    {bet.tipPosition}
                  </Badge>
                  {isWinning && <TrophyIcon className="w-3 h-3 text-yellow-500" />}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">#{bet.horseNumber}</span>
                  <span className="text-gray-600 max-w-24 truncate" title={bet.horseName}>
                    {bet.horseName}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Odds:</span>
                  <span className="font-medium">${bet.odds.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Stake:</span>
                  <span className="font-medium">${bet.stake.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Return:</span>
                  <span className="font-medium">${bet.return.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">P/L:</span>
                  <span className={`font-bold ${
                    profitLoss > 0 ? 'text-green-600' : 
                    profitLoss < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {profitLoss >= 0 ? '+' : ''}${profitLoss.toFixed(2)}
                  </span>
                </div>
                <Badge 
                  variant="outline" 
                  className={`text-xs ${
                    bet.result === 'win' ? 'text-green-700 bg-green-100 border-green-300' :
                    bet.result === 'place' ? 'text-blue-700 bg-blue-100 border-blue-300' :
                    'text-red-700 bg-red-100 border-red-300'
                  }`}
                >
                  {bet.result.toUpperCase()}
                </Badge>
              </div>
            </div>
          );
        })}
        
        {/* Summary row */}
        <div className="mt-3 pt-2 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Total for this race:</span>
            <div className="flex items-center gap-4">
              <span className="text-gray-600">
                Stake: ${strategyApplication.bets.reduce((sum, bet) => sum + bet.stake, 0).toFixed(2)}
              </span>
              <span className="text-gray-600">
                Return: ${strategyApplication.bets.reduce((sum, bet) => sum + bet.return, 0).toFixed(2)}
              </span>
              <span className={`font-bold ${
                strategyApplication.profit > 0 ? 'text-green-600' : 
                strategyApplication.profit < 0 ? 'text-red-600' : 'text-gray-600'
              }`}>
                P/L: {strategyApplication.profit >= 0 ? '+' : ''}${strategyApplication.profit.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border-b">
        <div className="text-sm text-gray-600">
          {selectedDate ? `Showing results for ${selectedDate}` : 'Showing all dates'}
          {appliedStrategy && (
            <span className="ml-2 text-blue-600">
              • Strategy: {appliedStrategy.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Date:</label>
          <Select value={selectedDate} onValueChange={(val) => setSelectedDate(val === '__ALL__' ? '' : val)}>
            <SelectTrigger className="w-48 px-2">
              <SelectValue placeholder="All dates" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">All</SelectItem>
              {availableDates.map((d) => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedDate && (
            <button
              type="button"
              onClick={() => setSelectedDate('')}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <TooltipProvider delayDuration={300} skipDelayDuration={100}>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {/* Selection column */}
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Select
                </th>
                
                {/* Strategy bet indicator */}
                {showStrategyColumns && appliedStrategy && (
                  <th className="px-2 py-3 text-center text-xs font-medium text-blue-600 uppercase tracking-wider">
                    Bet
                  </th>
                )}
                
                {/* Basic race info columns */}
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center gap-1">
                    Date
                    {getSortIcon('date')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('track')}
                >
                  <div className="flex items-center gap-1">
                    Track
                    {getSortIcon('track')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('race')}
                >
                  <div className="flex items-center gap-1">
                    Race #
                    {getSortIcon('race')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('horses')}
                >
                  <div className="flex items-center gap-1">
                    Horses
                    {getSortIcon('horses')}
                  </div>
                </th>

                {/* Strategy columns */}
                {showStrategyColumns && appliedStrategy && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-blue-600 uppercase tracking-wider">
                      Strategy Applied
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-blue-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('strategyStake')}
                    >
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        Stake
                        {getSortIcon('strategyStake')}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-blue-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('strategyProfit')}
                    >
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        Profit
                        {getSortIcon('strategyProfit')}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-blue-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('strategyRoi')}
                    >
                      <div className="flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        ROI
                        {getSortIcon('strategyRoi')}
                      </div>
                    </th>
                  </>
                )}

                {/* Expand column */}
                <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRaces.map((race, index) => {
                const raceId = race.id || `race-${index}`;
                const isSelected = isRowSelected(raceId);
                const isExpanded = isRowExpanded(raceId);
                
                // Parse race data for display
                const parts = raceId.split('-');
                const raceNumber = parts[parts.length - 4] || '';
                const date = getRaceDate(race) || '';
                const trackNameDisplay = race.meetingName || parts.slice(0, parts.length - 4).join(' ');
                const numberOfRunners = race.betfairResults?.numberOfRunners ?? '-';

                // Strategy metrics
                const strategyApplication = race.strategyApplications?.[0];
                const calculatedMetrics = race.calculatedMetrics || {};

                return (
                  <>
                    <tr 
                      key={raceId} 
                      className={`
                        ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                        ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''}
                        ${strategyApplication?.applied ? 
                          calculatedMetrics.profit > 0 ? 'border-l-4 border-green-500' :
                          calculatedMetrics.profit < 0 ? 'border-l-4 border-red-500' :
                          'border-l-4 border-gray-400' : ''
                        }
                        hover:bg-gray-100 cursor-pointer transition-colors
                      `}
                      onClick={() => handleRowSelect(race)}
                    >
                      {/* Selection checkbox */}
                      <td className="px-2 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleRowSelect(race)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>

                      {/* Strategy bet indicator */}
                      {showStrategyColumns && appliedStrategy && (
                        <td className="px-2 py-3 text-center">
                          {strategyApplication?.applied ? (
                            <div className="flex items-center justify-center">
                              <div className={`w-3 h-3 rounded-full ${
                                calculatedMetrics.profit > 0 ? 'bg-green-500' :
                                calculatedMetrics.profit < 0 ? 'bg-red-500' :
                                'bg-gray-400'
                              }`} title={
                                calculatedMetrics.profit > 0 ? 'Winning bet' :
                                calculatedMetrics.profit < 0 ? 'Losing bet' :
                                'Break-even bet'
                              } />
                            </div>
                          ) : (
                            <div className="w-3 h-3 rounded-full bg-gray-200" title="No bet placed" />
                          )}
                        </td>
                      )}

                      {/* Basic race info */}
                      <td className="px-4 py-3 text-sm text-gray-900">{date}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{trackNameDisplay}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">R{raceNumber}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{numberOfRunners}</td>

                      {/* Strategy columns */}
                      {showStrategyColumns && appliedStrategy && (
                        <>
                          <td className="px-4 py-3 text-sm">
                            {renderStrategyIndicator(race)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {strategyApplication?.applied ? `$${calculatedMetrics.totalStake?.toFixed(2) || '0.00'}` : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">
                            {strategyApplication?.applied ? (
                              <span className={calculatedMetrics.profit > 0 ? 'text-green-600' : 
                                             calculatedMetrics.profit < 0 ? 'text-red-600' : 'text-gray-600'}>
                                {calculatedMetrics.profit >= 0 ? '+' : ''}${calculatedMetrics.profit?.toFixed(2) || '0.00'}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">
                            {strategyApplication?.applied ? (
                              <span className={calculatedMetrics.roi > 0 ? 'text-green-600' : 
                                             calculatedMetrics.roi < 0 ? 'text-red-600' : 'text-gray-600'}>
                                {(calculatedMetrics.roi * 100)?.toFixed(1) || '0.0'}%
                              </span>
                            ) : '-'}
                          </td>
                        </>
                      )}

                      {/* Expand button */}
                      <td className="px-2 py-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowExpansion(raceId);
                          }}
                          className="h-6 w-6 p-0"
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </td>
                    </tr>

                    {/* Expanded row details */}
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan={showStrategyColumns && appliedStrategy ? 10 : 6} className="px-4 py-4">
                          <div className="space-y-4">
                            {/* Strategy bet details */}
                            {showStrategyColumns && appliedStrategy && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-2">Strategy Bets</h4>
                                {renderBetDetails(race)}
                              </div>
                            )}

                            {/* Race tips and results */}
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Race Information</h4>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                                <div>
                                  <span className="text-gray-600">Track:</span>
                                  <span className="ml-2 font-medium">{trackNameDisplay}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Race:</span>
                                  <span className="ml-2 font-medium">R{raceNumber}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Horses:</span>
                                  <span className="ml-2 font-medium">{numberOfRunners}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Date:</span>
                                  <span className="ml-2 font-medium">{date}</span>
                                </div>
                              </div>

                              {/* Tips breakdown */}
                              {(race.earlyPredictionData?.firstFourData || race.predictionData?.firstFourData) && (
                                <div className="mt-4">
                                  <h5 className="text-sm font-medium text-gray-900 mb-2">Original Tips</h5>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Early tips */}
                                    {race.earlyPredictionData?.firstFourData && (
                                      <div>
                                        <h6 className="text-xs font-medium text-blue-600 mb-2">Morning Tips (T1-T4)</h6>
                                        <div className="space-y-1">
                                          {race.earlyPredictionData.firstFourData.slice(0, 4).map((tip, idx) => (
                                            <div key={idx} className="flex items-center justify-between text-xs bg-blue-50 p-2 rounded">
                                              <div className="flex items-center gap-2">
                                                <Badge variant="outline" className="text-blue-600 bg-blue-100">
                                                  T{idx + 1}
                                                </Badge>
                                                <span>#{tip.formattedName?.split('.')[0] || 'N/A'}</span>
                                                <span className="text-gray-600 truncate max-w-20">
                                                  {tip.formattedName?.split('.').slice(1).join('.').trim() || 'Unknown'}
                                                </span>
                                              </div>
                                              <span className="font-medium">
                                                ${(tip.lastPriceTraded || tip.currentPrice || 0).toFixed(2)}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Race time tips */}
                                    {race.predictionData?.firstFourData && (
                                      <div>
                                        <h6 className="text-xs font-medium text-purple-600 mb-2">Race-time Tips (T5-T8)</h6>
                                        <div className="space-y-1">
                                          {race.predictionData.firstFourData.slice(0, 4).map((tip, idx) => (
                                            <div key={idx} className="flex items-center justify-between text-xs bg-purple-50 p-2 rounded">
                                              <div className="flex items-center gap-2">
                                                <Badge variant="outline" className="text-purple-600 bg-purple-100">
                                                  T{idx + 5}
                                                </Badge>
                                                <span>#{tip.formattedName?.split('.')[0] || 'N/A'}</span>
                                                <span className="text-gray-600 truncate max-w-20">
                                                  {tip.formattedName?.split('.').slice(1).join('.').trim() || 'Unknown'}
                                                </span>
                                              </div>
                                              <span className="font-medium">
                                                ${(tip.lastPriceTraded || tip.currentPrice || 0).toFixed(2)}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Race results */}
                              {race.betfairResults && (
                                <div className="mt-4">
                                  <h5 className="text-sm font-medium text-gray-900 mb-2">Race Results</h5>
                                  <div className="flex items-center gap-4 text-sm">
                                    {race.betfairResults.winner && (
                                      <div className="flex items-center gap-2">
                                        <TrophyIcon className="w-4 h-4 text-yellow-500" />
                                        <span className="text-gray-600">Winner:</span>
                                        <span className="font-medium">
                                          #{getHorseNumber(race.betfairResults.winner.selectionId, [
                                            ...(race.earlyPredictionData?.firstFourData || []),
                                            ...(race.predictionData?.firstFourData || [])
                                          ])}
                                        </span>
                                        <span className="text-green-600 font-medium">
                                          ${race.betfairResults.winner.startingPrice?.toFixed(2) || 'N/A'}
                                        </span>
                                      </div>
                                    )}
                                    {race.betfairResults.placeGetters && race.betfairResults.placeGetters.length > 0 && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-gray-600">Places:</span>
                                        {race.betfairResults.placeGetters
                                          .filter(p => p.selectionId !== race.betfairResults.winner?.selectionId)
                                          .slice(0, 2)
                                          .map((placer, idx) => (
                                            <span key={idx} className="text-blue-600 font-medium">
                                              #{getHorseNumber(placer.selectionId, [
                                                ...(race.earlyPredictionData?.firstFourData || []),
                                                ...(race.predictionData?.firstFourData || [])
                                              ])} (${placer.startingPrice?.toFixed(2) || 'N/A'})
                                            </span>
                                          ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </TooltipProvider>
      </div>

      {(!Array.isArray(filteredRaces) || filteredRaces.length === 0) && (
        <div className="text-center py-8 text-gray-500">
          No race data available{selectedDate ? ` for ${selectedDate}` : ''}
        </div>
      )}

      {/* Strategy summary footer */}
      {showStrategyColumns && appliedStrategy && Array.isArray(filteredRaces) && filteredRaces.length > 0 && (
        <div className="border-t bg-gray-50 p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">
              Strategy Performance Summary ({selectedDate || 'All dates'})
            </h4>
            <div className="flex items-center gap-6 text-sm">
              {(() => {
                const appliedRaces = filteredRaces.filter(race => race?.strategyApplications?.[0]?.applied);
                const totalStake = appliedRaces.reduce((sum, race) => sum + (race?.calculatedMetrics?.totalStake || 0), 0);
                const totalReturn = appliedRaces.reduce((sum, race) => sum + (race?.calculatedMetrics?.totalReturn || 0), 0);
                const totalProfit = totalReturn - totalStake;
                const roi = totalStake > 0 ? (totalProfit / totalStake) * 100 : 0;
                const winningRaces = appliedRaces.filter(race => (race?.calculatedMetrics?.profit || 0) > 0);
                const winRate = appliedRaces.length > 0 ? (winningRaces.length / appliedRaces.length) * 100 : 0;

                return (
                  <>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Races Bet</div>
                      <div className="font-medium">{appliedRaces.length} / {filteredRaces.length}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Total Stake</div>
                      <div className="font-medium">${totalStake.toFixed(2)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Total Return</div>
                      <div className="font-medium">${totalReturn.toFixed(2)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Net Profit</div>
                      <div className={`font-bold ${
                        totalProfit > 0 ? 'text-green-600' : 
                        totalProfit < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {totalProfit >= 0 ? '+' : ''}${totalProfit.toFixed(2)}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">ROI</div>
                      <div className={`font-bold ${
                        roi > 0 ? 'text-green-600' : 
                        roi < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {roi.toFixed(1)}%
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Win Rate</div>
                      <div className="font-medium">{winRate.toFixed(1)}%</div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default EnhancedResultsTable;